import * as React from 'react'
import { useFocusable, FocusContext } from '@noriginmedia/norigin-spatial-navigation'
import { Focusable } from './Focusable'
import { ChevronGlyph } from './Chevron'

export interface ScrollRowProps {
  /** Row items — each DIRECT child becomes one fixed-width, horizontally scrollable cell. */
  children: React.ReactNode;
  /** Extra class(es) on the outer container. */
  className?: string;
  /** Accessible label for the scrollable region (e.g. the row's title). */
  label?: string;
  /**
   * Freeze the row: no native scroll, no chevrons. Used while a row shows loading
   * skeletons — there is nothing real to scroll to yet, so the placeholder row
   * stays put instead of letting the viewer scroll a wall of shimmer.
   */
  locked?: boolean;
}

// Sub-pixel slack so a fractional scrollLeft near an edge doesn't flicker the chevrons.
const EDGE_TOLERANCE = 1

/**
 * A single horizontal row that scrolls its children. Scrolling is native
 * (touch swipe, trackpad, scrollbar); on hover the left/right chevrons appear —
 * but only in a direction that can actually scroll — and each click advances by
 * exactly one item width. Styled through the shell's design tokens.
 */
export function ScrollRow ({ children, className, label, locked = false }: ScrollRowProps) {
  // The row is a spatial-navigation container grouping the cell leaves. It stays
  // a *participating* node (do NOT pass `focusable: false`) — that is what lets
  // up/down bubble out of a cell and find the sibling rows; focusing a row then
  // delegates down to its remembered column.
  const { ref: containerRef, focusKey } = useFocusable({
    trackChildren: true,
    saveLastFocusedChild: true,
  })
  const viewportRef = React.useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = React.useState(false)
  const [canScrollRight, setCanScrollRight] = React.useState(false)

  // NOTE: the row deliberately does NOT claim focus on mount. This UI is also
  // used with a mouse and a finger, where an unrequested focus ring is noise —
  // so focus stays unset until the viewer presses an arrow (see focus.ts) or
  // tabs in. Nothing here needs to know about that policy.

  const sync = React.useCallback(() => {
    const el = viewportRef.current
    if (!el) return
    const maxScrollLeft = el.scrollWidth - el.clientWidth
    setCanScrollLeft(el.scrollLeft > EDGE_TOLERANCE)
    setCanScrollRight(el.scrollLeft < maxScrollLeft - EDGE_TOLERANCE)
  }, [])

  // Size the chevrons to the leading media block of a row item (the tile image),
  // not the full item height — the caption below the image should stay uncovered.
  // Published as a CSS var so the height stays in sync with the responsive width.
  const measureMedia = React.useCallback(() => {
    const el = viewportRef.current
    const container = containerRef.current
    if (!el || !container) return
    // Each cell is `.mlsg-focusable` > <Link> > media block, so the leading
    // media is three levels down from the viewport (one deeper than a bare cell).
    const media = el.firstElementChild?.firstElementChild?.firstElementChild as HTMLElement | null
    const height = media?.getBoundingClientRect().height
    container.style.setProperty('--mlsg-scrollrow-media-height', height ? `${height}px` : '100%')
  }, [])

  React.useEffect(() => {
    const el = viewportRef.current
    if (!el) return
    sync()
    measureMedia()
    el.addEventListener('scroll', sync, { passive: true })
    // Re-evaluate when the viewport or its content resizes — responsive width
    // changes and late-loading tiles shift both the scrollable extent and the
    // media height.
    const observer = new ResizeObserver(() => { sync(); measureMedia() })
    observer.observe(el)
    for (const child of Array.from(el.children)) observer.observe(child)
    return () => {
      el.removeEventListener('scroll', sync)
      observer.disconnect()
    }
  }, [sync, measureMedia, children])

  const scrollByStep = (direction: 1 | -1) => {
    const el = viewportRef.current
    if (!el) return
    const first = el.firstElementChild as HTMLElement | null
    const gap = parseFloat(getComputedStyle(el).columnGap) || 0
    // One element width (incl. the flex gap) — a single tile per click.
    const step = first ? first.getBoundingClientRect().width + gap : el.clientWidth
    el.scrollBy({ left: direction * step, behavior: 'smooth' })
  }

  return (
    <FocusContext.Provider value={focusKey}>
      <div ref={containerRef} className={['mlsg-scrollrow', locked ? 'mlsg-scrollrow--locked' : '', className].filter(Boolean).join(' ')}>
        <button
          type='button'
          className='mlsg-scrollrow__chevron mlsg-scrollrow__chevron--left'
          aria-label='Scroll left'
          disabled={locked || !canScrollLeft}
          onClick={() => scrollByStep(-1)}
        >
          <ChevronGlyph orientation='left' />
        </button>
        <div
          className='mlsg-scrollrow__viewport'
          ref={viewportRef}
          {...(label ? { role: 'group', 'aria-label': label } : {})}
        >
          {/* Each direct child becomes a remote-navigable cell. Non-element
              children (e.g. the empty-state message) pass through untouched. */}
          {React.Children.map(children, child =>
            React.isValidElement(child) ? <Focusable>{child}</Focusable> : child
          )}
        </div>
        <button
          type='button'
          className='mlsg-scrollrow__chevron mlsg-scrollrow__chevron--right'
          aria-label='Scroll right'
          disabled={locked || !canScrollRight}
          onClick={() => scrollByStep(1)}
        >
          <ChevronGlyph orientation='right' />
        </button>
      </div>
    </FocusContext.Provider>
  )
}
